import { action, observable, makeObservable } from 'mobx';
import axios from 'axios';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { getMappedUsersForDropdownMenu, isEmailInvalid } from '../utils/helpers';
import { END_USERS_ROLE } from '../utils/constants';
import { errorAlert, successAlert } from '../utils/alerts';

const { REACT_APP_API_URL } = process.env;

class ContactStore {
  constructor(profileStore) {
    makeObservable(this, {
      phoneVerificationNumber: observable,
      phoneVerificationCode: observable,
      emailVerificationEmail: observable,
      emailVerificationError: observable,
      emailVerificationCode: observable,
      listProfessionalUsers: observable,
      shouldDisplaySelectSuccessorModal: observable,
      loadingIsLastCompanyAdmin: observable,
      sendPhoneVerification: action,
      sendEmailVerification: action,
      sendUpdatePhone: action,
      phoneUpdateSuccess: action.bound,
      setPhoneVerificationCode: action,
      setEmailVerificationCode: action,
      emailUpdateSuccess: action.bound,
      sendUpdateEmail: action,
      getUsersListIfAdminIsLastCompanyAdmin: action,
      assignUserAsCompanyAdmin: action
    });

    this.profileStore = profileStore;
  }

  phoneVerificationNumber = null;

  phoneVerificationCode = null;

  emailVerificationEmail = null;

  emailVerificationError = null;

  emailVerificationCode = null;

  listProfessionalUsers = [];

  shouldDisplaySelectSuccessorModal = false;

  loadingIsLastCompanyAdmin = false;

  async sendPhoneVerification(values) {
    this.phoneVerificationNumber = values.phone;

    const dataToSave = {
      phone: this.phoneVerificationNumber,
    };

    const authToken = await this.profileStore.getAuthToken();

    // send verification request
    try {
      await axios.post(`${REACT_APP_API_URL}/me/updatePhone`, dataToSave, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      return true;
    } catch (error) {
      return false;
    }
  }

  async sendEmailVerification(values) {
    this.emailVerificationEmail = values.email;

    const dataToSave = {
      email: this.emailVerificationEmail,
    };
    const authToken = await this.profileStore.getAuthToken();
    // send verification request
    try {
      await axios.post(`${REACT_APP_API_URL}/me/changeEmailRequest`, dataToSave, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      return true;
    } catch (error) {
      return false;
    }
  }

  async sendUpdatePhone(t) {
    // send verification request
    const dataToSave = {
      code: this.phoneVerificationCode,
      phone: this.phoneVerificationNumber,
    };

    const authToken = await this.profileStore.getAuthToken();

    try {
      this.phoneVerificationCode = null;
      await axios.post(`${REACT_APP_API_URL}/me/updatePhoneConfirm`, dataToSave, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      this.phoneUpdateSuccess();
      successAlert(t('contact.phoneChanged'));
      return true;
    } catch (error) {
      errorAlert(t('verifyPhoneModal.phoneError'));
      return false;
    }
  }

  phoneUpdateSuccess() {
    this.profileStore.profileData.phone = this.phoneVerificationNumber;
    this.phoneVerificationNumber = null;
    this.profileStore.rootStore.sendUpdateNotification();
  }

  setPhoneVerificationCode(code) {
    this.phoneVerificationCode = code;
  }

  setEmailVerificationCode(code) {
    this.emailVerificationError = null;
    this.emailVerificationCode = code;
  }

  emailUpdateSuccess() {
    this.profileStore.profileData.email = this.emailVerificationEmail;
    this.emailVerificationEmail = null;
    this.profileStore.rootStore.sendUpdateNotification();
  }

  async sendUpdateEmail(t) {
    // send verification request
    const dataToSave = {
      code: this.emailVerificationCode,
    };

    const authToken = await this.profileStore.getAuthToken();

    try {
      this.emailVerificationCode = null;
      await axios.post(`${REACT_APP_API_URL}/me/changeEmailConfirm`, dataToSave, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      this.emailUpdateSuccess();
      successAlert(t('contact.emailChanged'));
      return true;
    } catch (error) {
      errorAlert(t('verifyPhoneModal.error'));
      return false;
    }
  }

  validateEmail(values, t) {
    const errors = {};

    if (!values.email || isEmailInvalid(values.email)) {
      errors.email = t('email.validation');
    }

    return errors;
  }

  validatePhone(values, t) {
    const errors = {};
    if (this.connection !== END_USERS_ROLE) {
      if (!values.phone) {
        errors.phone = t('phone.validation');
      } else {
        const parsedPhone = parsePhoneNumberFromString(values.phone, 'CH');

        if (!parsedPhone || !parsedPhone.isPossible()) {
          errors.phone = t('phone.validation');
        }
      }
    }

    return errors;
  }

  async getUsersListIfAdminIsLastCompanyAdmin() {
    this.loadingIsLastCompanyAdmin = true;
    const authToken = await this.profileStore.getAuthToken();
    const { data } = await axios.get(`${REACT_APP_API_URL}/me/isLastCompanyAdmin`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    const { isLastCompanyAdmin, users } = data;

    if (isLastCompanyAdmin) {
      this.listProfessionalUsers = getMappedUsersForDropdownMenu(users);
    }

    this.shouldDisplaySelectSuccessorModal = isLastCompanyAdmin;
    this.loadingIsLastCompanyAdmin = false;

    return data;
  }

  async assignUserAsCompanyAdmin(assigneeId) {
    this.loadingIsLastCompanyAdmin = true;
    try {
      const authToken = await this.profileStore.getAuthToken();
      const response = await axios.post(
        `${REACT_APP_API_URL}/users/admin`,
        { assignee: assigneeId },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (response.status === 200) {
        this.shouldDisplaySelectSuccessorModal = false;
        this.loadingIsLastCompanyAdmin = false;
        return true;
      }
    } catch (error) {
      return false;
    }
  }
}

export default ContactStore;
